import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// One-shot script loader. Returns a promise that resolves once google.maps.places is ready.
let _loaderPromise = null;
function loadGoogle() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (_loaderPromise) return _loaderPromise;
  _loaderPromise = new Promise((resolve, reject) => {
    if (!API_KEY) {
      reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY missing"));
      return;
    }
    // Global callback the Maps API will invoke once google.maps.places is fully ready.
    const cbName = "__fieldlinkGmapsReady";
    window[cbName] = () => {
      resolve(window.google);
      delete window[cbName];
    };
    const existing = document.querySelector("script[data-fieldlink-gmaps]");
    if (existing) {
      // Another instance already injected the script and is waiting; piggy-back via poll.
      const poll = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(poll);
          resolve(window.google);
        }
      }, 80);
      return;
    }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&callback=${cbName}&loading=async`;
    s.async = true;
    s.defer = true;
    s.dataset.fieldlinkGmaps = "1";
    s.onerror = () => reject(new Error("Google Maps script failed to load"));
    document.head.appendChild(s);
  });
  return _loaderPromise;
}

/**
 * AddressAutocomplete — NZ-restricted, shadcn-style address input.
 * Props:
 *  - value           current displayed text
 *  - onChange(text)  fired on each keystroke (the free-text address string)
 *  - onSelect(place) fired when a suggestion is picked → { address, lat, lng }
 *  - placeholder, testid, className, disabled
 */
export function AddressAutocomplete({ value, onChange, onSelect, placeholder, testid, className, disabled, id, includePlaces = false, inlineSuggestions = false }) {
  const [ready, setReady] = useState(!!(window.google?.maps?.places));
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const sessionTokenRef = useRef(null);
  const autocompleteSvcRef = useRef(null);
  const placesSvcRef = useRef(null);
  const containerRef = useRef(null);
  const lastQueryRef = useRef("");
  const debounceRef = useRef(null);
  const requestRef = useRef(0);
  useEffect(() => () => { clearTimeout(debounceRef.current); requestRef.current += 1; }, []);
  useEffect(() => { requestRef.current += 1; setSuggestions([]); setOpen(false); setLoading(false); }, [value]);

  // Load Google script once
  useEffect(() => {
    let cancelled = false;
    if (!ready) {
      loadGoogle()
        .then((g) => {
          if (cancelled) return;
          autocompleteSvcRef.current = new g.maps.places.AutocompleteService();
          const div = document.createElement("div");
          placesSvcRef.current = new g.maps.places.PlacesService(div);
          sessionTokenRef.current = new g.maps.places.AutocompleteSessionToken();
          setReady(true);
        })
        .catch((e) => setError(e.message));
    } else if (!autocompleteSvcRef.current) {
      autocompleteSvcRef.current = new window.google.maps.places.AutocompleteService();
      placesSvcRef.current = new window.google.maps.places.PlacesService(document.createElement("div"));
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return () => { cancelled = true; };
  }, [ready]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) { requestRef.current += 1; clearTimeout(debounceRef.current); setLoading(false); setOpen(false); }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = (text) => {
    if (!autocompleteSvcRef.current || !text || text.length < 3) {
      setSuggestions([]);
      return;
    }
    const request = ++requestRef.current;
    setLoading(true);
    autocompleteSvcRef.current.getPlacePredictions(
      {
        input: text,
        sessionToken: sessionTokenRef.current,
        componentRestrictions: { country: "nz" },
        ...(includePlaces ? {} : { types: ["address"] }),
      },
      (preds, status) => {
        if (request !== requestRef.current) return;
        setLoading(false);
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !preds) {
          setSuggestions([]);
          if (status !== "ZERO_RESULTS") setError(`Location search unavailable (${status}). You can type a location manually.`);
          return;
        }
        setError(null);
        setSuggestions(preds);
        setOpen(true);
        setActiveIdx(-1);
      }
    );
  };

  const onInput = (e) => {
    requestRef.current += 1;
    setSuggestions([]); setOpen(false); setLoading(false);
    const txt = e.target.value;
    onChange?.(txt);
    lastQueryRef.current = txt;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (lastQueryRef.current === txt) fetchSuggestions(txt);
    }, 220);
  };

  const pick = (pred) => {
    if (!placesSvcRef.current) return;
    clearTimeout(debounceRef.current);
    const request = ++requestRef.current;
    placesSvcRef.current.getDetails(
      { placeId: pred.place_id, fields: ["formatted_address", "geometry", "name", "place_id"], sessionToken: sessionTokenRef.current },
      (place, status) => {
        if (request !== requestRef.current) return;
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) { setError("Could not select this location. You can enter it manually."); return; }
        const address = includePlaces ? pred.description || place.formatted_address : place.formatted_address || pred.description;
        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();
        onChange?.(address);
        onSelect?.({ address, lat, lng, place_id: place.place_id || pred.place_id });
        setOpen(false);
        setSuggestions([]);
        // Rotate session token for the next search
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
    );
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0) {
        e.preventDefault();
        pick(suggestions[activeIdx]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault(); e.stopPropagation(); requestRef.current += 1; clearTimeout(debounceRef.current); setLoading(false);
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        role="combobox" aria-expanded={open} aria-autocomplete="list" aria-controls={`${id || testid}-results`} aria-activedescendant={open && activeIdx >= 0 ? `${id || testid}-option-${activeIdx}` : undefined}
        type="text"
        value={value || ""}
        onChange={onInput}
        onKeyDown={onKey}
        onFocus={() => suggestions.length && setOpen(true)}
        placeholder={placeholder || (ready ? "Start typing an address…" : error ? "Address (autocomplete unavailable)" : "Loading suggestions…")}
        className={className}
        data-testid={testid}
        disabled={disabled}
        autoComplete="off"
      />
      {loading && (
        <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 animate-spin pointer-events-none" />
      )}
      {includePlaces && error && <p role="status" className="text-xs text-amber-700 mt-1">Location search unavailable. Enter a location manually.</p>}
      {open && suggestions.length > 0 && (
        <div id={`${id || testid}-results`} role="listbox" className={`${inlineSuggestions ? "relative max-h-48" : "absolute left-0 right-0 max-h-72"} z-50 mt-1 bg-white border border-zinc-300 shadow-lg overflow-y-auto rounded-sm`} data-testid={`${testid}-suggestions`}>
          {suggestions.map((p, i) => (
            <button
              type="button"
              key={p.place_id}
              id={`${id || testid}-option-${i}`} role="option" aria-selected={i === activeIdx}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => pick(p)}
              className={`w-full text-left px-3 py-2 text-sm border-b border-zinc-100 last:border-b-0 flex items-start gap-2 transition-colors ${
                i === activeIdx ? "bg-orange-50" : "hover:bg-zinc-50"
              }`}
              data-testid={`${testid}-suggestion-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#FF5F15] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-zinc-900 break-words">{p.structured_formatting?.main_text || p.description}</div>
                {p.structured_formatting?.secondary_text && (
                  <div className="text-xs text-zinc-500 break-words">{p.structured_formatting.secondary_text}</div>
                )}
              </div>
            </button>
          ))}
          <div className="text-[9px] font-mono text-zinc-400 uppercase tracking-widest px-3 py-1.5 border-t border-zinc-200 bg-zinc-50 text-right">Powered by Google</div>
        </div>
      )}
    </div>
  );
}
